import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Gauge, RefreshCw, ChevronDown, ChevronUp, ExternalLink, Eye, HandCoins, Map, Pencil, Plug, Plus, Search, ShieldCheck, Ticket, Trash2, Users, Wifi, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, rupiah } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useRouters } from "../context/RouterContext";
import PinConfirm from "../components/PinConfirm";
import { AgentSearchSelect } from "../components/AgentSearchSelect";
import { CekKomisiButton } from "../components/CekKomisiButton";
import { MikrotikLogDialog } from "../components/MikrotikLogDialog";
import { BandwidthCapDialog } from "../components/BandwidthCapDialog";
import { BulkBandwidthCapDialog } from "../components/BulkBandwidthCapDialog";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";

const BULAN_JT = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const fmtJT = (jt) => {
  if (!jt) return "-";
  const d = new Date(jt);
  if (isNaN(d.getTime())) return jt;
  return `${String(d.getDate()).padStart(2, "0")}-${BULAN_JT[d.getMonth()]}-${String(d.getFullYear()).slice(2)}`;
};
const hariMenuju = (jt) => {
  if (!jt) return null;
  const d = new Date(jt);
  if (isNaN(d.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.ceil((d - today) / 86400000);
};
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const EMPTY = { name: "", host: "", port: 8728, winbox_port: "", username: "", password: "", location: "", dns_name: "", target_omset: 0, is_active: true, use_ssl: false, assigned_emails: [] };

const KABUPATEN = [
  "Kab. Kubu Raya", "Kab. Landak", "Kab. Bengkayang", "Kab. Sambas", "Kab. Sanggau",
  "Kab. Ketapang", "Kab. Sekadau", "Kab. Sintang", "Kab. Melawi",
];

const LokasiSelect = ({ value, onChange, testid }) => {
  const options = value && !KABUPATEN.includes(value) ? [...KABUPATEN, value] : KABUPATEN;
  return (
    <Select value={value || "-"} onValueChange={(v) => onChange(v === "-" ? "" : v)}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Pilih Kabupaten" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="-">Pilih Kabupaten</SelectItem>
        {options.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};
const BULAN = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const fmtBulan = (ym) => { const [y, m] = ym.split("-"); return `${BULAN[Number(m) - 1]} ${y}`; };

function AssignEmailList({ users, values, onChange, testid }) {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return users;
    return users.filter((u) => u.email.toLowerCase().includes(t) || (u.name || "").toLowerCase().includes(t));
  }, [users, q]);
  return (
    <div className="space-y-1.5 col-span-2">
      <Label>Tautkan ke Email Pengguna (hanya email ini yang bisa melihat router ini)</Label>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="Cari email atau nama mitra..." value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} />
      </div>
      <div className="max-h-36 overflow-y-auto border border-border rounded-xl divide-y divide-border" data-testid={testid}>
        {shown.map((u) => (
          <label key={u.email} className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent/50 cursor-pointer">
            <input type="checkbox" className="accent-blue-600 w-4 h-4"
              checked={(values || []).includes(u.email)}
              onChange={(e) => onChange(e.target.checked ? [...(values || []), u.email] : (values || []).filter((x) => x !== u.email))}
              data-testid={`${testid}-${u.email}`} />
            <span className="font-mono text-xs">{u.email}</span>
            <span className="text-xs text-muted-foreground ml-auto">{u.name}</span>
          </label>
        ))}
        {!shown.length && <div className="px-3 py-2.5 text-xs text-muted-foreground">{users.length ? "Email tidak ditemukan" : "Belum ada pengguna terdaftar"}</div>}
      </div>
    </div>
  );
}

export default function Routers() {
  const { user: me } = useAuth();
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const isSuperAdmin = me?.role === "admin";
  const [fixingProxy, setFixingProxy] = useState(false);
  const fixIsolirProxy = async () => {
    if (!window.confirm("Jalankan perbaikan ke SEMUA router sekarang? Rule web-proxy/redirect isolir akan dihapus; blok internet PPPoE isolir tetap berjalan.")) return;
    setFixingProxy(true);
    try {
      const { data } = await api.post("/routers/perbaiki-isolir-proxy");
      toast.success(data.note, { duration: 10000 });
    } catch (e) { toast.error(errMsg(e)); } finally { setFixingProxy(false); }
  };
  const navigate = useNavigate();
  const { routers, refresh, setSelected } = useRouters();
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [shareRouter, setShareRouter] = useState(null);
  const [logRouter, setLogRouter] = useState(null);
  const [capRouter, setCapRouter] = useState(null);
  const [bulkCapOpen, setBulkCapOpen] = useState(false);
  const [shares, setShares] = useState([]);
  const [sharePin, setSharePin] = useState("");
  const [shareExpanded, setShareExpanded] = useState(null);

  const saveShares = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/verify-pin", { pin: sharePin });
      const payload = shares.filter((s) => s.agent_code && Number(s.value) > 0)
        .map((s) => ({ agent_code: s.agent_code, type: s.type, value: Number(s.value) }));
      const { data } = await api.put(`/routers/${shareRouter.id}/commission-shares`, { shares: payload });
      toast.success(`Bagi komisi ${shareRouter.name} disimpan — ${data.shares.length} penerima`);
      setShareRouter(null);
      setSharePin("");
      refresh();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  const [monthly, setMonthly] = useState({ months: [], per_router: {} });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editRouter, setEditRouter] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [editPin, setEditPin] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [testing, setTesting] = useState({});
  const [massTesting, setMassTesting] = useState(false);
  const [health, setHealth] = useState(null);
  useEffect(() => { api.get("/health-check").then((r) => setHealth(r.data)).catch(() => {}); }, []);
  const [kits, setKits] = useState([]);
  useEffect(() => { api.get("/netdata/starlink").then((r) => setKits(r.data || [])).catch(() => {}); }, []);
  const offlineIds = new Set((health?.offline || []).map((o) => o.router_id));
  const [results, setResults] = useState({});
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [routerSugOpen, setRouterSugOpen] = useState(false);
  const [routerSugForce, setRouterSugForce] = useState(false);
  const routerSugRef = useRef(null);
  useEffect(() => {
    const close = (e) => { if (routerSugRef.current && !routerSugRef.current.contains(e.target)) { setRouterSugOpen(false); setRouterSugForce(false); } };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const qRouterSug = query.trim().toLowerCase();
  const routerSuggestions = qRouterSug
    ? routers.filter((r) => `${r.name || ""} ${r.host || ""}`.toLowerCase().includes(qRouterSug)).slice(0, 8)
    : routers.slice(0, 8);
  const showRouterSug = routerSugOpen && (qRouterSug.length >= 3 || routerSugForce);
  const [statusFilter, setStatusFilter] = useState("all");
  const [perPage, setPerPage] = useState("10");
  const [kabFilter, setKabFilter] = useState("semua");
  const [page, setPage] = useState(0);
  // Kelompokkan KIT Starlink per cabang; suffix "[SL=n]" di Data Starlink = Starlink ke-n (urutan rolling anti-FUP)
  const kitsByCabang = useMemo(() => {
    const map = {};
    for (const s of kits) {
      const msl = /\[SL=(\d+)\]/i.exec(s.nama_cabang || "");
      const base = (s.nama_cabang || "").replace(/\s*\[SL=\d+\]/i, "").trim().toUpperCase();
      if (!base) continue;
      (map[base] = map[base] || []).push({ ...s, seq: msl ? Number(msl[1]) : 1 });
    }
    Object.values(map).forEach((arr) => arr.sort((a, b) => a.seq - b.seq));
    return map;
  }, [kits]);
  const [linkRouter, setLinkRouter] = useState(null);
  const [linkEmails, setLinkEmails] = useState([]);
  const [linkPin, setLinkPin] = useState("");
  const [onlineCounts, setOnlineCounts] = useState({});
  const [svcRecap, setSvcRecap] = useState(null);
  const [svcFilter, setSvcFilter] = useState("");
  const loadSvc = () => api.get("/routers-service-recap").then((r) => setSvcRecap(r.data)).catch(() => {});
  useEffect(() => { loadSvc(); }, []);
  useEffect(() => { if (!svcRecap?.scan?.running) return undefined; const t = setInterval(loadSvc, 5000); return () => clearInterval(t); }, [svcRecap?.scan?.running]);
  const scanSvc = async () => { try { const { data } = await api.post("/routers-service-scan"); toast.success(data.queued ? "Pemindaian profile hotspot & PPP dimulai di latar belakang" : `Pemindaian masih berjalan (${data.done}/${data.total})`); setTimeout(loadSvc, 1500); } catch (e) { toast.error(errMsg(e)); } };

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/agents").then((r) => setAgents(r.data)).catch(() => {});
    api.get("/reports/cabang-bulanan").then((r) => setMonthly(r.data)).catch(() => {});
  }, [isAdmin]);

  const openEdit = (r) => {
    setEditRouter(r);
    setEditForm({ name: r.name, host: r.host, port: r.port, winbox_port: r.winbox_port || "", username: r.username, password: "", location: r.location || "", dns_name: r.dns_name || "", target_omset: r.target_omset || 0, is_active: r.is_active !== false, use_ssl: !!r.use_ssl, assigned_emails: r.assigned_emails || [] });
    setEditPin("");
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/verify-pin", { pin: editPin });
      await api.put(`/routers/${editRouter.id}`, { ...editForm, port: Number(editForm.port), winbox_port: Number(editForm.winbox_port) || 0, target_omset: Number(editForm.target_omset) });
      toast.success(`Cabang ${editForm.name} diperbarui`);
      setEditRouter(null);
      refresh();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/routers", { ...form, port: Number(form.port), winbox_port: Number(form.winbox_port) || 0, target_omset: Number(form.target_omset) });
      toast.success(`Cabang ${form.name} ditambahkan`);
      setOpen(false);
      setForm(EMPTY);
      refresh();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const test = async (r) => {
    setTesting((t) => ({ ...t, [r.id]: true }));
    try {
      const { data } = await api.post(`/routers/${r.id}/test`);
      setResults((x) => ({ ...x, [r.id]: { ok: true, ...data } }));
      toast.success(`Terhubung ke ${data.identity || r.name} (${data.board})`);
    } catch (err) {
      setResults((x) => ({ ...x, [r.id]: { ok: false, error: errMsg(err) } }));
      toast.error(errMsg(err));
    } finally {
      setTesting((t) => ({ ...t, [r.id]: false }));
    }
  };

  const testAll = async () => {
    setMassTesting(true);
    try {
      await api.post("/health-check/run", {});
      toast.success("Tes koneksi masal dimulai — menguji semua cabang, hasil ±1 menit. Laporan otomatis juga masuk Chat Sistem.");
      setTimeout(async () => {
        try {
          const { data } = await api.get("/health-check");
          const off = {};
          (data.offline || []).forEach((o) => {
            off[o.router_id] = { ok: false, error: o.status === "auth-failed" ? "Gagal login (kredensial salah)" : "Tidak online (router mati/timeout)" };
          });
          setResults((x) => {
            const next = { ...x };
            routers.forEach((r) => { next[r.id] = off[r.id] || { ok: true, identity: r.name }; });
            return next;
          });
          toast.info(`Tes selesai: ${data.summary?.ok ?? 0} online, ${(data.summary?.unreachable ?? 0) + (data.summary?.["auth-failed"] ?? 0)} tidak online.`);
        } catch { /* abaikan */ }
        setMassTesting(false);
      }, 55000);
    } catch (err) {
      toast.error(errMsg(err));
      setMassTesting(false);
    }
  };

  const fmtQuota = (b) => {
    if (!b) return "-";
    if (b >= 1073741824) return `${(b / 1073741824).toFixed(2)} GB`;
    return `${(b / 1048576).toFixed(1)} MB`;
  };

  const gotoOffline = () => {
    setStatusFilter("offline");
    setPage(0);
    setTimeout(() => document.querySelector("[data-testid=routers-table]")?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const doDelete = async () => {
    try {
      await api.delete(`/routers/${deleteTarget.id}`);
      toast.success("Cabang dihapus");
      setDeleteTarget(null);
      refresh();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const openLink = (r) => {
    setLinkRouter(r);
    setLinkEmails(r.assigned_emails || []);
    setLinkPin("");
  };

  const saveLink = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/auth/verify-pin", { pin: linkPin });
      const r = linkRouter;
      await api.put(`/routers/${r.id}`, {
        name: r.name, host: r.host, port: Number(r.port), winbox_port: Number(r.winbox_port) || 0,
        username: r.username, password: "", location: r.location || "", dns_name: r.dns_name || "",
        target_omset: Number(r.target_omset) || 0, is_active: r.is_active !== false, use_ssl: !!r.use_ssl,
        assigned_emails: linkEmails,
      });
      toast.success(`Tautan email cabang ${r.name} diperbarui (${linkEmails.length} email)`);
      setLinkRouter(null);
      refresh();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    const offlineSet = new Set((health?.offline || []).map((o) => o.router_id));
    const q = query.trim().toLowerCase();
    return routers.filter((r) => {
      if (statusFilter === "online" && offlineSet.has(r.id)) return false;
      if (statusFilter === "nonactive" && r.is_active !== false) return false;
      if (statusFilter === "offline" && !offlineSet.has(r.id)) return false;
      if (kabFilter !== "semua" && (r.location || "") !== kabFilter) return false;
      if (svcFilter && (svcRecap?.by_router?.[r.id] || "none") !== svcFilter) return false;
      if (!q) return true;
      return [r.name, r.host, r.dns_name, r.location].some((f) => (f || "").toLowerCase().includes(q));
    }).sort((a, b) => (b.quota_month_bytes || 0) - (a.quota_month_bytes || 0));
  }, [routers, query, statusFilter, health, kabFilter, svcFilter, svcRecap]);

  const per = perPage === "semua" ? Math.max(filtered.length, 1) : Number(perPage);
  const pageCount = Math.max(1, Math.ceil(filtered.length / per));
  const curPage = Math.min(page, pageCount - 1);
  const rows = filtered.slice(curPage * per, curPage * per + per);
  const rowsKey = rows.map((r) => r.id).join(",");
  const onlineTotals = rows.reduce((acc, r) => {
    const oc = onlineCounts[r.id];
    if (oc && !oc.error) { acc.hs += oc.hotspot; acc.ppp += oc.ppp; }
    return acc;
  }, { hs: 0, ppp: 0 });
  const kabRecap = useMemo(() => {
    const offlineSet = new Set((health?.offline || []).map((o) => o.router_id));
    const map = {};
    routers.forEach((r) => {
      const kab = r.location || "";
      if (!KABUPATEN.includes(kab)) return;
      if (!map[kab]) map[kab] = { total: 0, online: 0 };
      map[kab].total += 1;
      if (!offlineSet.has(r.id) && r.is_active !== false) map[kab].online += 1;
    });
    return KABUPATEN.filter((k) => map[k]).map((k) => [k, map[k]]);
  }, [routers, health]);
  useEffect(() => {
    if (!rowsKey) return undefined;
    const loadOnline = () => api.get(`/routers-online-counts?ids=${rowsKey}`).then((r) => setOnlineCounts((prev) => ({ ...prev, ...r.data }))).catch(() => {});
    loadOnline();
    const t = setInterval(loadOnline, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsKey]);

  if (!isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="routers-forbidden">
        <ShieldCheck className="w-10 h-10 text-rose-500" />
        <p className="text-muted-foreground text-sm">Hanya Admin Fasnet / Super Admin yang dapat mengelola cabang.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="routers-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Tabel Data {routers.length} Cabang Fasnet WiFi</h1>
          <div className="flex items-center gap-3 flex-wrap mt-1">
            {health?.checked_at && (
              <p className="text-xs font-mono" data-testid="routers-health-summary">
                <span className="text-emerald-500 font-semibold">Online {health.summary?.ok ?? 0}</span>
                <span className="text-muted-foreground">{"  |  "}</span>
                <span className="text-rose-400 font-semibold">Offline {(health.summary?.unreachable ?? 0) + (health.summary?.["auth-failed"] ?? 0)}</span>
                <button type="button" onClick={gotoOffline} className="ml-2 px-2 py-0.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-400 hover:bg-rose-500/20 active:scale-95 transition-all" data-testid="btn-goto-offline" title="Tampilkan hanya cabang offline">Lihat</button>
              </p>
            )}
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
              <SelectTrigger className="w-40 h-8 text-xs" data-testid="select-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua</SelectItem>
                <SelectItem value="online" data-testid="filter-online">Online (koneksi)</SelectItem>
                <SelectItem value="offline" data-testid="filter-offline">Offline (koneksi)</SelectItem>
                <SelectItem value="nonactive">Nonactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={kabFilter} onValueChange={(v) => { setKabFilter(v); setPage(0); }}>
              <SelectTrigger className="w-44" data-testid="select-kab-filter"><SelectValue placeholder="Semua Kabupaten" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="semua" data-testid="filter-kab-semua">Semua Kabupaten</SelectItem>
                {KABUPATEN.map((k) => <SelectItem key={k} value={k} data-testid={`filter-kab-${k.replace(/\W+/g, "-").toLowerCase()}`}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Button variant="outline" className="text-rose-400 border-rose-500/40 hover:bg-rose-500/10" disabled={fixingProxy} title="Hapus rule web-proxy isolir dari semua router (penyebab voucher hotspot ERR_TOO_MANY_REDIRECTS)" onClick={fixIsolirProxy} data-testid="btn-fix-isolir-proxy">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${fixingProxy ? "animate-spin" : ""}`} /> {fixingProxy ? "Memperbaiki..." : "Perbaiki Redirect Voucher"}
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="btn-add-router">
            <Plus className="w-4 h-4 mr-1.5" /> Tambah Cabang
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
          <Select value={perPage} onValueChange={(v) => { setPerPage(v); setPage(0); }}>
            <SelectTrigger className="w-20" data-testid="select-per-page"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["10", "25", "50", "100", "semua"].map((n) => <SelectItem key={n} value={n} data-testid={`per-page-opt-${n}`}>{n === "semua" ? "Semua" : n}</SelectItem>)}
            </SelectContent>
          </Select>
          <span>data per halaman</span>
          {filtered.length > per && (
            <div className="flex gap-1.5 ml-2">
              <Button size="sm" variant="outline" disabled={curPage === 0} onClick={() => setPage(curPage - 1)} data-testid="btn-prev-page-top">Sebelumnya</Button>
              <Button size="sm" variant="outline" disabled={curPage >= pageCount - 1} onClick={() => setPage(curPage + 1)} data-testid="btn-next-page-top">Berikutnya</Button>
            </div>
          )}
          <div className="relative ml-2" ref={routerSugRef}>
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(0); setRouterSugOpen(true); setRouterSugForce(false); }}
              onFocus={() => setRouterSugOpen(true)}
              placeholder="Cari cabang..." className="pl-9 pr-16 w-56" data-testid="input-router-search" autoComplete="off" />
            <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
              {query && (
                <button type="button" title="Hapus pencarian" onClick={() => { setQuery(""); setPage(0); setRouterSugOpen(false); setRouterSugForce(false); }}
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" data-testid="btn-router-search-clear">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              <button type="button" title="Cari cabang" onClick={() => { setRouterSugForce(true); setRouterSugOpen(true); }}
                className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors" data-testid="btn-router-search">
                <Search className="w-3.5 h-3.5" />
              </button>
            </div>
            {showRouterSug && routerSuggestions.length > 0 && (
              <div className="absolute z-30 left-0 mt-1 rounded-lg border border-border bg-popover shadow-lg max-h-56 overflow-y-auto min-w-64" data-testid="router-suggestions">
                {routerSuggestions.map((r) => (
                  <button key={r.id || r.name} type="button" onClick={() => { setQuery(r.name); setPage(0); setRouterSugOpen(false); setRouterSugForce(false); }}
                    className="w-full px-3 py-2 text-left hover:bg-accent/60 transition-colors border-b border-border/50 last:border-0"
                    data-testid={`router-sug-option-${(r.name || "").replace(/\s+/g, "-")}`}>
                    <div className="text-xs font-medium truncate">{r.name}</div>
                    {r.host && <div className="text-[10px] font-mono text-muted-foreground truncate">{r.host}{r.port ? `:${r.port}` : ""}</div>}
                  </button>
                ))}
              </div>
            )}
            {showRouterSug && !routerSuggestions.length && (
              <div className="absolute z-30 left-0 mt-1 rounded-lg border border-border bg-popover shadow-lg px-3 py-2 text-xs text-muted-foreground min-w-56" data-testid="router-sug-empty">Cabang tidak ditemukan</div>
            )}
          </div>
          <Button variant="outline" onClick={testAll} disabled={massTesting} title="Tes koneksi semua cabang sekaligus" data-testid="btn-test-all-routers">
            <Activity className={`w-4 h-4 mr-1.5 ${massTesting ? "animate-pulse text-sky-500" : ""}`} /> {massTesting ? "Menguji Semua..." : "Tes Koneksi Masal"}
          </Button>
          <Button variant="outline" onClick={() => setBulkCapOpen(true)} className="text-amber-400 border-amber-500/40 hover:bg-amber-500/10" title="Terapkan batas bandwidth yang sama ke beberapa cabang sekaligus" data-testid="btn-bulk-cap">
            <Gauge className="w-4 h-4 mr-1.5" /> Batas Bandwidth Massal
          </Button>
          <Button variant="outline" onClick={() => navigate("/semua-wifi-rumahan")} className="text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10" title="Buka semua data konsumen WiFi Rumahan dari seluruh cabang" data-testid="btn-semua-wifi-rumahan">
            <Wifi className="w-4 h-4 mr-1.5" /> Semua WiFi Rumahan
          </Button>
          {isAdmin && <CekKomisiButton isSuper={me?.role === "admin"} />}
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-sky-500/30 bg-sky-500/10 text-xs font-mono" title="Total user online cabang di halaman ini" data-testid="online-total">
            <span className="inline-flex items-center gap-1 text-sky-400"><span className="w-1.5 h-1.5 rounded-full bg-sky-400" />HS {onlineTotals.hs}</span>
            <span className="text-muted-foreground">•</span>
            <span className="inline-flex items-center gap-1 text-emerald-400"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />PPP {onlineTotals.ppp}</span>
          </span>
        </div>
      </div>

      {svcRecap && (
        <div className="flex gap-2 flex-wrap items-center" data-testid="service-recap">
          {[["WIFI Voucheran", "hs", svcRecap.voucheran, "text-sky-400 border-sky-500/30 bg-sky-500/10"],
            ["WIFI Rumahan", "ppp", svcRecap.rumahan, "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"],
            ["WIFI Voucheran + Rumahan", "both", svcRecap.both, "text-violet-400 border-violet-500/30 bg-violet-500/10"],
            ["Belum ada profile", "none", svcRecap.none, "text-muted-foreground border-border bg-card"]].map(([l, key, n, cls]) => (
            <button type="button" key={l} onClick={() => { setSvcFilter(svcFilter === key ? "" : key); setPage(0); }} className={`px-3 py-1.5 rounded-lg text-[11px] font-mono border transition-colors ${cls} ${svcFilter === key ? "ring-2 ring-[#F04E23] ring-offset-1 ring-offset-background" : "hover:brightness-125"}`} title="Klik untuk memfilter daftar cabang" data-testid={`svc-${l.replace(/\W+/g, "-").toLowerCase()}`}>{l}: <b>{n}</b> cabang</button>
          ))}
          <button type="button" onClick={scanSvc} disabled={svcRecap.scan?.running} className="px-3 py-1.5 rounded-lg text-[11px] border border-border hover:bg-accent disabled:opacity-60 flex items-center gap-1" title="Baca ulang daftar profile hotspot & PPP dari semua router" data-testid="svc-scan">
            <RefreshCw className={`w-3 h-3 ${svcRecap.scan?.running ? "animate-spin" : ""}`} /> {svcRecap.scan?.running ? `Memindai ${svcRecap.scan.done}/${svcRecap.scan.total}…` : `Pindai Profile (${svcRecap.scanned}/${svcRecap.total} terpindai)`}
          </button>
        </div>
      )}

      {kabRecap.length > 0 && (
        <div className="flex gap-2 flex-wrap" data-testid="kab-recap">
          {kabRecap.map(([kab, v]) => (
            <button
              key={kab}
              type="button"
              onClick={() => { setKabFilter(kabFilter === kab ? "semua" : kab); setPage(0); }}
              className={`px-3 py-1.5 rounded-full text-[11px] font-mono border transition-colors ${kabFilter === kab ? "bg-[#F04E23]/15 text-[#F04E23] border-[#F04E23]/40" : "bg-card text-muted-foreground border-border hover:bg-accent/50 hover:text-foreground"}`}
              data-testid={`chip-kab-${kab.replace(/\W+/g, "-").toLowerCase()}`}
            >
              {kab}: {v.total} cabang • {v.online} online
            </button>
          ))}
        </div>
      )}
      <Card className="bg-card border-border shadow-sm overflow-x-auto">
        <Table className="min-w-[980px]" data-testid="routers-table">
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">#</TableHead>
              <TableHead>Nama Cabang</TableHead>
              <TableHead>DNS</TableHead>
              <TableHead className="text-right">Target Omset</TableHead>
              {monthly.current && <TableHead className="text-right">Bulan Ini<span className="block text-[10px] font-normal text-muted-foreground">{fmtBulan(monthly.current)}</span></TableHead>}
              {(monthly.months || []).map((m, i) => (
                <TableHead key={m} className="text-right">Bulan -{i + 1}<span className="block text-[10px] font-normal text-muted-foreground">{fmtBulan(m)}</span></TableHead>
              ))}
              <TableHead>Status</TableHead>
              <TableHead>User Online</TableHead>
              <TableHead className="text-right">Kuota Bulan Berjalan</TableHead>
              <TableHead className="text-right">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!rows.length && (
              <TableRow><TableCell colSpan={11} className="text-center py-10 text-muted-foreground" data-testid="routers-empty">
                Belum ada cabang. Klik "Tambah Cabang" untuk menghubungkan MikroTik Anda.
              </TableCell></TableRow>
            )}
            {rows.map((r, idx) => {
              const res = results[r.id];
              const pm = monthly.per_router?.[r.id] || {};
              const conn = results[r.id] ? (results[r.id].ok ? "online" : "offline") : health?.checked_at ? (offlineIds.has(r.id) ? "offline" : "online") : "unknown";
              return (
                <TableRow key={r.id} data-testid={`router-row-${r.name}`}>
                  <TableCell className="font-mono text-muted-foreground">{curPage * per + idx + 1}</TableCell>
                  <TableCell>
                    <div className="font-semibold">{r.name}</div>
                    {r.location && <div className="text-xs text-muted-foreground">{r.location}</div>}
                    {(r.assigned_emails || []).length > 0 ? (
                      <button type="button" onClick={() => openLink(r)} className="inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-mono bg-sky-500/10 text-sky-400 border border-sky-500/20 hover:bg-sky-500/20 active:scale-95 transition-all" title={`Klik untuk kelola: ${(r.assigned_emails || []).join(", ")}`} data-testid={`router-linked-${r.name}`}>
                        <Users className="w-3 h-3" /> {(r.assigned_emails || []).length} agen tertaut
                      </button>
                    ) : (
                      <button type="button" onClick={() => openLink(r)} className="inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-mono text-muted-foreground border border-dashed border-border hover:bg-accent/50 active:scale-95 transition-all" data-testid={`router-linked-${r.name}`}>
                        <Users className="w-3 h-3" /> Belum tertaut
                      </button>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 flex-wrap">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" className="text-sky-600 border-sky-500/40 hover:bg-sky-500/10" data-testid={`btn-voucher-${r.name}`}>
                            <Ticket className="w-3.5 h-3.5 mr-1" /> Data Voucher
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" data-testid={`menu-voucher-${r.name}`}>
                          <DropdownMenuItem onClick={() => { setSelected(r.id); navigate("/vouchers"); }} data-testid={`menu-voucher-view-${r.name}`}>
                            <Eye className="w-4 h-4 mr-2" /> Tampilkan Data Voucher
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSelected(r.id); navigate("/vouchers?profiles=1"); }} data-testid={`menu-voucher-add-profile-${r.name}`}>
                            <Eye className="w-4 h-4 mr-2" /> Tampilkan Profile Voucher
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSelected(r.id); navigate("/vouchers?add-voucher=1"); }} data-testid={`menu-voucher-add-voucher-${r.name}`}>
                            <Ticket className="w-4 h-4 mr-2" /> Buat Voucher Baru
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <div>
                        {r.dns_name && <div className="text-sm">{r.dns_name}</div>}
                        <div className="text-xs font-mono text-muted-foreground">({r.host}:{r.port})</div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button size="sm" variant="outline" className="text-emerald-400 border-emerald-500/40 hover:bg-emerald-500/10" data-testid={`btn-wifirumahan-${r.name}`}>
                            <Wifi className="w-3.5 h-3.5 mr-1" /> WiFi Rumahan
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" data-testid={`menu-wifi-${r.name}`}>
                          <DropdownMenuItem onClick={() => { setSelected(r.id); navigate("/ppp"); }} data-testid={`menu-wifi-view-${r.name}`}>
                            <Eye className="w-4 h-4 mr-2" /> Tampilkan Data WiFi Rumahan
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSelected(r.id); navigate("/ppp?profiles=1"); }} data-testid={`menu-wifi-add-profile-${r.name}`}>
                            <Eye className="w-4 h-4 mr-2" /> Tampilkan Profile WiFi Rumahan
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => { setSelected(r.id); navigate("/ppp?add-secret=1"); }} data-testid={`menu-wifi-add-secret-${r.name}`}>
                            <Wifi className="w-4 h-4 mr-2" /> Buat Secret Baru
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setLogRouter(r)} data-testid={`menu-wifi-log-${r.name}`}>
                            <Activity className="w-4 h-4 mr-2 text-amber-500" /> Log MikroTik (kendala PPP)
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <Button size="sm" variant="outline" className="text-sky-400 border-sky-500/40 hover:bg-sky-500/10" title="Peta ODC-ODP cabang ini" onClick={() => { setSelected(r.id); navigate("/peta-odc-odp"); }} data-testid={`btn-peta-odc-odp-${r.name}`}>
                        <Map className="w-3.5 h-3.5 mr-1" /> Peta ODC-ODP
                      </Button>
                      {isSuperAdmin && (
                        <Button size="sm" variant="outline" className="text-violet-400 border-violet-500/40 hover:bg-violet-500/10" title="Bagi Komisi — atur pembagian komisi tiap transaksi cabang ini" onClick={() => { setShareRouter(r); setShares((r.commission_shares || []).map((s) => ({ ...s }))); setSharePin(""); }} data-testid={`btn-share-komisi-${r.name}`}>
                          <HandCoins className="w-3.5 h-3.5 mr-1" /> Bagi Komisi
                          {(r.commission_shares || []).length > 0 && <span className="ml-1 px-1.5 rounded-full bg-violet-500/15 text-[10px] font-mono">{r.commission_shares.length}</span>}
                        </Button>
                      )}
                      {isSuperAdmin && (r.commission_shares || []).length > 0 && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-violet-400" title={shareExpanded === r.id ? "Tutup detail bagi komisi" : "Lihat detail bagi komisi"} onClick={() => setShareExpanded(shareExpanded === r.id ? null : r.id)} data-testid={`btn-share-expand-${r.name}`}>
                          {shareExpanded === r.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                      )}
                      {isSuperAdmin && shareExpanded === r.id && (r.commission_shares || []).length > 0 && (
                      <div className="w-full mt-1.5 rounded-lg border border-violet-500/20 bg-violet-500/5 divide-y divide-violet-500/10" data-testid={`share-info-${r.name}`}>
                        {r.commission_shares.map((s) => {
                          const ag = agents.find((a) => a.code === s.agent_code);
                          return (
                            <div key={s.agent_code} className="flex items-center gap-2 px-2.5 py-1.5 text-[11px]" data-testid={`share-chip-${r.name}-${s.agent_code}`}>
                              <span className="px-1.5 py-0.5 rounded font-mono bg-violet-500/15 text-violet-300 border border-violet-500/25">{s.agent_code}</span>
                              <span className="flex-1 min-w-0 truncate font-medium">{ag?.name || "-"}</span>
                              <span className="font-mono font-semibold text-violet-300 whitespace-nowrap">{s.type === "percent" ? `${s.value}% omset` : `Rp ${Number(s.value).toLocaleString("id-ID")}/transaksi`}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                      <Button size="sm" variant="outline" className="text-sky-400 border-sky-500/40 hover:bg-sky-500/10" title="Trafik Monitor — kecepatan & paket interface router ini" onClick={() => { setSelected(r.id); navigate("/trafik"); }} data-testid={`btn-traffic-${r.name}`}>
                        <Activity className="w-3.5 h-3.5 mr-1" /> Trafik Monitor
                      </Button>
                      <Button size="sm" variant="outline" className={r.bandwidth_cap?.mbps ? "text-amber-400 border-amber-500/40 hover:bg-amber-500/10" : "text-muted-foreground"} title="Batas total bandwidth cabang ini dari MikroTik agar Starlink/ISP tidak tersedot" onClick={() => setCapRouter(r)} data-testid={`btn-cap-${r.name}`}>
                        <Gauge className="w-3.5 h-3.5 mr-1" /> {r.bandwidth_cap?.mbps ? `Batas ${r.bandwidth_cap.mbps} Mbps` : "Batas Bandwidth"}
                      </Button>
                      {(kitsByCabang[(r.name || "").toUpperCase().trim()] || []).map((k) => {
                        const sisa = hariMenuju(k.jatuh_tempo);
                        const overdue = sisa !== null && sisa < 0;
                        return (
                          <span key={k.id} className="inline-flex flex-col leading-tight px-2 py-1 rounded-lg border border-border bg-background/60" title={`Starlink ${k.seq} (${k.sn_kit || "SN belum diisi"}) — tanggal rolling/ganti pakai: ${fmtJT(k.jatuh_tempo)}. Ganti ke Starlink berikutnya pada tanggal ini untuk menghindari FUP.`} data-testid={`router-kit-${r.name}-${k.seq}`}>
                            <span className="font-mono text-[10px] text-muted-foreground">SL{k.seq} • {k.sn_kit || "-"}</span>
                            <span className={`font-mono text-[10px] ${overdue ? "text-rose-400 font-bold" : "text-amber-400"}`}>{fmtJT(k.jatuh_tempo)}</span>
                          </span>
                        );
                      })}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {(() => {
                      const tgt = r.target_omset || 0; const last = pm[monthly.months?.[0]]?.total || 0; const pct = tgt ? Math.round((last / tgt) * 100) : null;
                      const cls = pct === null ? "" : pct >= 100 ? "text-emerald-500" : pct >= 70 ? "text-amber-400" : "text-rose-400";
                      return <div title={pct === null ? "Target belum diisi" : `Bulan lalu tercapai ${pct}% (${rupiah(last)})`} data-testid={`router-target-${r.name}`}><div className={`font-bold ${cls}`}>{rupiah(tgt)}</div>{pct !== null && <div className={`text-[10px] ${cls}`}>bln lalu {pct}%</div>}</div>;
                    })()}
                  </TableCell>
                  {monthly.current && (() => {
                    const cur = pm[monthly.current]?.total || 0; const tgt = r.target_omset || 0; const pct = tgt ? Math.min(100, Math.round((cur / tgt) * 100)) : 0;
                    return (
                      <TableCell className="text-right" data-testid={`router-cur-${r.name}`}>
                        <div className="font-mono font-bold text-sky-400">{rupiah(cur)}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">Qty: {pm[monthly.current]?.qty || 0}{tgt ? ` · ${Math.round((cur / tgt) * 100)}%` : ""}</div>
                        {tgt > 0 && <div className="h-1 mt-1 rounded-full bg-border overflow-hidden"><div className={`h-full ${pct >= 100 ? "bg-emerald-500" : "bg-sky-400"}`} style={{ width: `${pct}%` }} /></div>}
                      </TableCell>
                    );
                  })()}
                  {(monthly.months || []).map((m) => (
                    <TableCell key={m} className="text-right">
                      <div className="font-mono">{rupiah(pm[m]?.total || 0)}</div>
                      <div className="text-[10px] font-mono text-muted-foreground">Qty: {pm[m]?.qty || 0}</div>
                    </TableCell>
                  ))}
                  <TableCell>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-mono border ${conn === "online" ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : conn === "offline" ? "bg-rose-500/10 text-rose-400 border-rose-500/20" : "bg-slate-500/10 text-slate-400 border-slate-500/20"}`} data-testid={`router-status-${r.name}`}>
                      {conn === "online" ? "Online" : conn === "offline" ? "Offline" : "Belum dites"}
                    </span>
                    {res && (
                      <div className={`text-[10px] font-mono mt-1 ${res.ok ? "text-emerald-500" : "text-rose-400"}`} data-testid={`router-conn-${r.name}`}>
                        {res.ok ? `Online • ${res.board || ""} • CPU ${res.cpu_load}%` : "Koneksi gagal"}
                      </div>
                    )}
                  </TableCell>
                  <TableCell data-testid={`router-online-${r.name}`}>
                    {(() => {
                      const oc = onlineCounts[r.id];
                      if (!oc) return <span className="text-xs font-mono text-muted-foreground">…</span>;
                      if (oc.error) return <span className="text-xs font-mono text-rose-400">-</span>;
                      return (
                        <div className="text-xs font-mono whitespace-nowrap">
                          <button type="button" onClick={() => { setSelected(r.id); navigate("/hotspot-online"); }} className="inline-flex items-center gap-1 text-sky-400 hover:bg-sky-500/10 rounded px-1 -mx-1 transition-colors" title="Lihat hotspot online cabang ini" data-testid={`link-hs-online-${r.name}`}><span className="w-1.5 h-1.5 rounded-full bg-sky-400" />HS {oc.hotspot}</button>
                          <span className="text-muted-foreground mx-1">•</span>
                          <button type="button" onClick={() => { setSelected(r.id); navigate("/ppp-online"); }} className="inline-flex items-center gap-1 text-emerald-400 hover:bg-emerald-500/10 rounded px-1 transition-colors" title="Lihat PPP online cabang ini" data-testid={`link-ppp-online-${r.name}`}><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />PPP {oc.ppp}</button>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs" data-testid={`router-quota-${r.name}`}>
                    {fmtQuota(r.quota_month_bytes)}
                    <div className={`text-[10px] ${Number(r.quota_day_bytes) >= 150 * 1024 ** 3 ? "text-rose-500 font-bold" : "text-muted-foreground"}`} data-testid={`router-quota-today-${r.name}`}>Hari ini: {fmtQuota(r.quota_day_bytes)}</div>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap space-x-1">
                    <Button size="icon" variant="ghost" title="Tes Koneksi" onClick={() => test(r)} disabled={testing[r.id]} data-testid={`btn-test-${r.name}`}>
                      <Plug className={`w-4 h-4 ${testing[r.id] ? "animate-pulse text-sky-500" : ""}`} />
                    </Button>
                    <Button size="icon" variant="ghost" title="Buka Dashboard Cabang" onClick={() => { setSelected(r.id); navigate("/"); }} data-testid={`btn-use-${r.name}`}>
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" title="Edit" onClick={() => openEdit(r)} data-testid={`btn-edit-router-${r.name}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" className="text-rose-400" title="Hapus" onClick={() => setDeleteTarget(r)} data-testid={`btn-delete-router-${r.name}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {filtered.length > per && (
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border text-xs text-muted-foreground">
            <span data-testid="routers-count">Menampilkan {curPage * per + 1}–{Math.min((curPage + 1) * per, filtered.length)} dari {filtered.length} cabang</span>
            <div className="flex gap-1.5">
              <Button size="sm" variant="outline" disabled={curPage === 0} onClick={() => setPage(curPage - 1)} data-testid="btn-prev-page">Sebelumnya</Button>
              <Button size="sm" variant="outline" disabled={curPage >= pageCount - 1} onClick={() => setPage(curPage + 1)} data-testid="btn-next-page">Berikutnya</Button>
            </div>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dialog-add-router">
          <DialogHeader><DialogTitle>Tambah Cabang (Router MikroTik)</DialogTitle>
            <DialogDescription>Isi koneksi RouterOS API dan tautkan router ke email pengguna yang boleh melihatnya.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nama Cabang</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="FASNET-SG1" data-testid="input-router-name" /></div>
              <div className="space-y-1.5"><Label>Lokasi</Label><LokasiSelect value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="input-router-location" /></div>
              <div className="space-y-1.5"><Label>Nama DNS (opsional)</Label><Input value={form.dns_name} onChange={(e) => setForm({ ...form, dns_name: e.target.value })} placeholder="fas.net" data-testid="input-router-dns" /></div>
              <div className="space-y-1.5"><Label>Target Omset/Bulan (Rp)</Label><Input type="number" min="0" value={form.target_omset} onChange={(e) => setForm({ ...form, target_omset: e.target.value })} data-testid="input-router-target" /></div>
              <div className="space-y-1.5"><Label>Host / IP</Label><Input value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} required placeholder="sg1.fasnetskyfiber.my.id" data-testid="input-router-host" /></div>
              <div className="space-y-1.5"><Label>Port API</Label><Input type="number" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} required data-testid="input-router-port" /></div>
              <div className="space-y-1.5"><Label>Port Winbox (opsional)</Label><Input type="number" min="0" value={form.winbox_port} onChange={(e) => setForm({ ...form, winbox_port: e.target.value })} placeholder="Contoh: 50025" data-testid="input-router-winbox-port" /></div>
              <div className="space-y-1.5"><Label>Username</Label><Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required data-testid="input-router-username" /></div>
              <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="input-router-password" /></div>
              <AssignEmailList users={users} values={form.assigned_emails} onChange={(v) => setForm({ ...form, assigned_emails: v })} testid="assign-add" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.use_ssl} onCheckedChange={(v) => setForm({ ...form, use_ssl: v, port: v ? 8729 : 8728 })} data-testid="switch-router-ssl" />
              <Label>Gunakan API-SSL (port 8729)</Label>
            </div>
            <Button type="submit" className="w-full" disabled={saving} data-testid="btn-save-router">
              {saving ? "Menyimpan..." : "Simpan Cabang"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!editRouter} onOpenChange={() => setEditRouter(null)}>
        <DialogContent data-testid="dialog-edit-router">
          <DialogHeader><DialogTitle>Edit Cabang: {editRouter?.name}</DialogTitle>
            <DialogDescription>Ubah koneksi atau penautan email. Password dikosongkan jika tidak diubah.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nama Cabang</Label><Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required data-testid="input-edit-router-name" /></div>
              <div className="space-y-1.5"><Label>Lokasi</Label><LokasiSelect value={editForm.location} onChange={(v) => setEditForm({ ...editForm, location: v })} testid="input-edit-router-location" /></div>
              <div className="space-y-1.5"><Label>Nama DNS (opsional)</Label><Input value={editForm.dns_name} onChange={(e) => setEditForm({ ...editForm, dns_name: e.target.value })} data-testid="input-edit-router-dns" /></div>
              <div className="space-y-1.5"><Label>Target Omset/Bulan (Rp)</Label><Input type="number" min="0" value={editForm.target_omset} onChange={(e) => setEditForm({ ...editForm, target_omset: e.target.value })} data-testid="input-edit-router-target" /></div>
              <div className="space-y-1.5"><Label>Host / IP (tanpa port)</Label><Input value={editForm.host} onChange={(e) => setEditForm({ ...editForm, host: e.target.value })} required placeholder="sg1.fasnetskyfiber.my.id" data-testid="input-edit-router-host" /></div>
              <div className="space-y-1.5"><Label>Port API</Label><Input type="number" value={editForm.port} onChange={(e) => setEditForm({ ...editForm, port: e.target.value })} required data-testid="input-edit-router-port" /></div>
              <div className="space-y-1.5"><Label>Port Winbox (opsional)</Label><Input type="number" min="0" value={editForm.winbox_port} onChange={(e) => setEditForm({ ...editForm, winbox_port: e.target.value })} placeholder="Contoh: 50025" data-testid="input-edit-router-winbox-port" /></div>
              <div className="space-y-1.5"><Label>Username</Label><Input value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} required data-testid="input-edit-router-username" /></div>
              <div className="space-y-1.5"><Label>Password (kosongkan jika tetap)</Label><Input type="password" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} placeholder="••••••••" data-testid="input-edit-router-password" /></div>
              <AssignEmailList users={users} values={editForm.assigned_emails} onChange={(v) => setEditForm({ ...editForm, assigned_emails: v })} testid="assign-edit" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={editForm.use_ssl} onCheckedChange={(v) => setEditForm({ ...editForm, use_ssl: v })} data-testid="switch-edit-router-ssl" />
              <Label>Gunakan API-SSL (port 8729)</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={editForm.is_active} onCheckedChange={(v) => setEditForm({ ...editForm, is_active: v })} data-testid="switch-edit-router-active" />
              <Label>Status Active (nonaktifkan untuk menyembunyikan dari filter Active)</Label>
            </div>
            <div className="space-y-1.5">
              <Label>PIN Admin (wajib untuk menyimpan perubahan)</Label>
              <Input type="password" inputMode="numeric" value={editPin} onChange={(e) => setEditPin(e.target.value)} required placeholder="Masukkan PIN" data-testid="input-edit-router-pin" />
            </div>
            <Button type="submit" className="w-full" disabled={saving} data-testid="btn-update-router">
              {saving ? "Menyimpan..." : "Simpan Perubahan"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!linkRouter} onOpenChange={() => setLinkRouter(null)}>
        <DialogContent data-testid="dialog-router-links">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-sky-400" /> Agen Tertaut: {linkRouter?.name}</DialogTitle>
            <DialogDescription>Centang email yang boleh melihat cabang ini. Hapus centang untuk melepas — tidak perlu membuka tiap agen.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveLink} className="space-y-3">
            <div className="grid grid-cols-1 gap-3">
              <AssignEmailList users={users} values={linkEmails} onChange={setLinkEmails} testid="link-emails" />
            </div>
            <div className="space-y-1.5">
              <Label>PIN Admin (wajib untuk menyimpan)</Label>
              <Input type="password" inputMode="numeric" value={linkPin} onChange={(e) => setLinkPin(e.target.value)} required placeholder="Masukkan PIN" data-testid="input-link-pin" />
            </div>
            <Button type="submit" className="w-full" disabled={saving} data-testid="btn-save-links">
              {saving ? "Menyimpan..." : "Simpan Tautan Email"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog open={!!shareRouter} onOpenChange={() => setShareRouter(null)}>
        <DialogContent className="max-w-lg" data-testid="dialog-share-komisi">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><HandCoins className="w-5 h-5 text-violet-400" /> Bagi Komisi: {shareRouter?.name}</DialogTitle>
            <DialogDescription>Setiap ada transaksi penjualan di cabang ini, komisi otomatis dibagikan ke akun-akun berikut sesuai pengaturan.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveShares} className="space-y-3">
            {shares.map((s, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`share-row-${i}`}>
                <AgentSearchSelect
                  options={agents.map((a) => ({ value: a.code, label: `${a.code} — ${a.name}`, search: `${a.code} ${a.name}` }))}
                  value={s.agent_code} onChange={(v) => setShares(shares.map((x, idx) => idx === i ? { ...x, agent_code: v } : x))}
                  placeholder="Agen penerima" testid={`share-agent-${i}`} triggerClassName="flex-1" />
                <select value={s.type} onChange={(e) => setShares(shares.map((x, idx) => idx === i ? { ...x, type: e.target.value } : x))} data-testid={`share-type-${i}`}
                  className="h-10 w-24 rounded-md border border-input bg-background px-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring">
                  <option value="percent">% Omset</option>
                  <option value="flat">Nominal</option>
                </select>
                <Input type="number" min="0" className="w-24" value={s.value} onChange={(e) => setShares(shares.map((x, idx) => idx === i ? { ...x, value: e.target.value } : x))} placeholder={s.type === "percent" ? "%" : "Rp"} data-testid={`share-value-${i}`} />
                <Button type="button" size="icon" variant="ghost" className="text-rose-400" onClick={() => setShares(shares.filter((_, idx) => idx !== i))} data-testid={`share-del-${i}`}><X className="w-4 h-4" /></Button>
              </div>
            ))}
            {!shares.length && <p className="text-xs text-muted-foreground" data-testid="share-empty">Belum ada penerima — komisi cabang ini tidak dibagikan.</p>}
            <Button type="button" variant="secondary" className="w-full" onClick={() => setShares([...shares, { agent_code: "", type: "percent", value: "" }])} data-testid="btn-share-add">
              <Plus className="w-4 h-4 mr-1.5" /> Tambah Penerima
            </Button>
            {shares.some((s) => s.type === "percent") && (
              <p className="text-[11px] font-mono text-muted-foreground" data-testid="share-total-pct">Total: {shares.filter((s) => s.type === "percent").reduce((a, s) => a + Number(s.value || 0), 0)}% dari setiap transaksi</p>
            )}
            <div className="space-y-1.5">
              <Label>PIN Super Admin (wajib untuk menyimpan)</Label>
              <Input type="password" inputMode="numeric" value={sharePin} onChange={(e) => setSharePin(e.target.value)} required placeholder="Masukkan PIN" data-testid="input-share-pin" />
            </div>
            <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-500 text-white" disabled={saving} data-testid="btn-save-shares">
              {saving ? "Menyimpan..." : "Simpan Bagi Komisi"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <PinConfirm
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title={`Hapus Cabang ${deleteTarget?.name}?`}
        description="Router cabang akan dihapus permanen dari sistem. Riwayat penjualan lama tetap tersimpan."
        confirmLabel="Ya, Hapus Cabang"
        destructive
        onConfirm={doDelete}
        testid="pin-delete-router"
      />
      <MikrotikLogDialog router={logRouter} onClose={() => setLogRouter(null)} />
      <BulkBandwidthCapDialog open={bulkCapOpen} routers={routers} onClose={(changed) => { setBulkCapOpen(false); if (changed) refresh(); }} />
      <BandwidthCapDialog router={capRouter} onClose={(changed) => { setCapRouter(null); if (changed) refresh(); }} />
    </div>
  );
}
